import React, { Component } from 'react';

import Header from '../../components/header';
import TitlePage from '../../components/title-page';

import {
	MAX_RESOURCES,
	MAX_FILE_SIZE_MB,
	MAX_FILE_SIZE_BYTES,
	fetchLibrary,
	createFolder,
	renameFolder,
	deleteFolder,
	uploadResource,
	moveResource,
	deleteResource,
	resourceErrorText
} from '../../services/user-resources';

// Resources: la biblioteca de imagenes del usuario. Un solo nivel de carpetas,
// con imagenes sueltas en la raiz, al estilo de Drive pero recortado: aqui solo
// entran imagenes y la cuenta tiene un tope de archivos (MAX_RESOURCES).
//
// La vista es una sola: `open_folder` a null es la raiz (carpetas + sueltas) y
// con un id es el interior de esa carpeta. No hay rutas por carpeta porque sin
// anidamiento no hay a donde navegar hacia atras salvo a la raiz.
export default class Resources extends Component {
	state = {
		loading: true,
		error: "",
		notice: "",
		folders: [],
		resources: [],
		quota: {used: 0, max: MAX_RESOURCES, max_file_size_mb: MAX_FILE_SIZE_MB},
		open_folder: null,
		uploading: false,
		upload_progress: null,
		modal_folder: "closed",
		folder_name: "",
		folder_being_renamed: null,
		modal_delete: "closed",
		delete_target: null,
		modal_move: "closed",
		move_target: null
	}

	fileInputRef = React.createRef()

	componentDidMount() {
		document.querySelector('body').classList.remove("no-scroll");
		this.loadLibrary();
	}

	componentWillUnmount() {
		// Los modales bloquean el scroll del body, que sobrevive a esta pagina.
		document.querySelector('body').classList.remove("no-scroll");
	}

	loadLibrary = async () => {
		try {
			const data = await fetchLibrary();

			this.setState({
				folders: data.folders || [],
				resources: data.resources || [],
				quota: data.quota || this.state.quota,
				loading: false
			});
		} catch (error) {
			console.log(error);
			this.setState({loading: false, error: resourceErrorText(error)});
		}
	}

	// ----------------------------------------------------
	// MODALES --------------------------------------------
	// ----------------------------------------------------

	modal = (name, status, payload) => {
		const next = {[name]: status, error: "", notice: ""};

		if (name === "modal_folder") {
			// El mismo modal crea y renombra: si viene carpeta, es un renombrado.
			next.folder_being_renamed = status === "opened" ? (payload || null) : null;
			next.folder_name = status === "opened" && payload ? payload.name : "";
		}

		if (name === "modal_delete") {
			next.delete_target = status === "opened" ? payload : null;
		}

		if (name === "modal_move") {
			next.move_target = status === "opened" ? payload : null;
		}

		this.setState(next);

		if (status === "opened") {
			document.querySelector('body').classList.add("no-scroll");
		} else {
			document.querySelector('body').classList.remove("no-scroll");
		}
	}

	onChange = (e) => {
		this.setState({[e.target.name]: e.target.value});
	}

	// ----------------------------------------------------
	// CARPETAS -------------------------------------------
	// ----------------------------------------------------

	saveFolder = async (e) => {
		e.preventDefault();

		const {folder_name, folder_being_renamed} = this.state;

		if (!folder_name.trim()) {
			this.setState({error: "The folder needs a name"});
			return;
		}

		try {
			if (folder_being_renamed) {
				await renameFolder(folder_being_renamed.id, folder_name.trim());
			} else {
				await createFolder(folder_name.trim());
			}

			this.modal('modal_folder', 'closed');
			await this.loadLibrary();
		} catch (error) {
			console.log(error);
			// El error se queda en el modal: cerrarlo perderia lo tecleado y el
			// caso tipico es un nombre repetido, que se arregla editandolo.
			this.setState({error: resourceErrorText(error)});
		}
	}

	// ----------------------------------------------------
	// SUBIDA ---------------------------------------------
	// ----------------------------------------------------

	pickFiles = () => {
		if (this.fileInputRef.current) {
			this.fileInputRef.current.click();
		}
	}

	onFilesPicked = async (e) => {
		const picked = Array.from(e.target.files || []);

		// El input se limpia ya: si no, elegir el mismo archivo otra vez no
		// dispara change y parece que la pagina se ha quedado colgada.
		e.target.value = "";

		if (picked.length === 0) {
			return;
		}

		await this.uploadFiles(picked);
	}

	uploadFiles = async (files) => {
		const {quota, open_folder} = this.state;
		const room = quota.max - quota.used;

		if (room <= 0) {
			this.setState({error: `You have reached the limit of ${quota.max} resources. Delete one to upload another, or contact Seat 7 if you require more files.`});
			return;
		}

		// Se parte el lote antes de empezar en vez de dejar que el servidor
		// rechace las ultimas: asi el aviso dice cuantas entran y cuantas no.
		const accepted = files.slice(0, room);
		const rejectedByQuota = files.length - accepted.length;

		const tooBig = accepted.filter(file => file.size > MAX_FILE_SIZE_BYTES);
		const notImages = accepted.filter(file => !file.type.startsWith('image/'));
		const valid = accepted.filter(file =>
			file.size <= MAX_FILE_SIZE_BYTES && file.type.startsWith('image/')
		);

		this.setState({uploading: true, error: "", notice: ""});

		const failed = [];
		let uploaded = 0;

		for (let i = 0; i < valid.length; i++) {
			this.setState({upload_progress: {current: i + 1, total: valid.length}});

			try {
				await uploadResource(valid[i], open_folder);
				uploaded++;
			} catch (error) {
				console.log(error);
				failed.push(`${valid[i].name}: ${resourceErrorText(error)}`);
			}
		}

		this.setState({uploading: false, upload_progress: null});
		await this.loadLibrary();

		// Un solo aviso que resume la tanda entera, en vez de un modal por fallo.
		const problems = [];

		if (rejectedByQuota > 0) {
			problems.push(`${rejectedByQuota} left out: only ${room} slot${room === 1 ? '' : 's'} left`);
		}

		if (tooBig.length > 0) {
			problems.push(`${tooBig.length} over ${MAX_FILE_SIZE_MB} MB`);
		}

		if (notImages.length > 0) {
			problems.push(`${notImages.length} not an image`);
		}

		problems.push(...failed);

		this.setState({
			notice: uploaded > 0 ? `${uploaded} image${uploaded === 1 ? '' : 's'} uploaded.` : "",
			error: problems.length > 0 ? problems.join('. ') : ""
		});
	}

	// ----------------------------------------------------
	// BORRAR Y MOVER -------------------------------------
	// ----------------------------------------------------

	confirmDelete = async () => {
		const {delete_target} = this.state;

		if (!delete_target) {
			return;
		}

		try {
			if (delete_target.type === "folder") {
				await deleteFolder(delete_target.folder.id);

				// Si estaba abierta la carpeta que se borra, hay que volver a la raiz
				// o la vista quedaria apuntando a algo que ya no existe.
				if (this.state.open_folder === delete_target.folder.id) {
					this.setState({open_folder: null});
				}
			} else {
				await deleteResource(delete_target.resource.id);
			}

			this.modal('modal_delete', 'closed');
			await this.loadLibrary();
		} catch (error) {
			console.log(error);
			this.modal('modal_delete', 'closed');
			this.setState({error: resourceErrorText(error)});
		}
	}

	moveTo = async (folder_id) => {
		const {move_target} = this.state;

		if (!move_target) {
			return;
		}

		try {
			await moveResource(move_target.id, folder_id);
			this.modal('modal_move', 'closed');
			await this.loadLibrary();
		} catch (error) {
			console.log(error);
			this.modal('modal_move', 'closed');
			this.setState({error: resourceErrorText(error)});
		}
	}

	copyUrl = (resource) => {
		// Para pegar la imagen en una descripcion sin tener que volver a subirla.
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(resource.url)
				.then(() => this.setState({notice: "Link copied to the clipboard.", error: ""}))
				.catch(() => this.setState({error: "Could not copy the link."}));
		} else {
			this.setState({error: "Copying is not available in this browser."});
		}
	}

	// ----------------------------------------------------
	// RENDER ---------------------------------------------
	// ----------------------------------------------------

	visibleResources = () => {
		const {resources, open_folder} = this.state;

		return resources.filter(resource =>
			open_folder === null
				? !resource.folder_id
				: resource.folder_id === open_folder
		);
	}

	openFolder = () => this.state.folders.find(folder => folder.id === this.state.open_folder) || null

	renderGrid() {
		const {folders, open_folder} = this.state;
		const items = this.visibleResources();
		const showFolders = open_folder === null;

		if (showFolders && folders.length === 0 && items.length === 0) {
			return (
				<div className="resources-empty">
					<p className="text">Your library is empty. Create a folder or upload your first image.</p>
				</div>
			);
		}

		if (!showFolders && items.length === 0) {
			return (
				<div className="resources-empty">
					<p className="text">This folder is empty. Upload an image to fill it.</p>
				</div>
			);
		}

		return (
			<div className="resources-grid">
				{showFolders && folders.map(folder => (
					<div className="resource-card folder-card" key={`folder-${folder.id}`}>
						<button
							type="button"
							className="card-open"
							onClick={() => this.setState({open_folder: folder.id, notice: "", error: ""})}
						>
							<span className="folder-icon" aria-hidden="true"></span>
							<span className="card-name">{folder.name}</span>
							<span className="card-meta">
								{folder.count} image{folder.count === 1 ? '' : 's'}
							</span>
						</button>

						<div className="card-actions">
							<button
								type="button"
								className="btn grey"
								aria-label={`Rename ${folder.name}`}
								onClick={() => this.modal('modal_folder', 'opened', folder)}
							>Rename</button>
							<button
								type="button"
								className="btn grey"
								aria-label={`Delete ${folder.name}`}
								onClick={() => this.modal('modal_delete', 'opened', {type: "folder", folder})}
							>Delete</button>
						</div>
					</div>
				))}

				{items.map(resource => (
					<div className="resource-card image-card" key={`resource-${resource.id}`}>
						<a
							className="card-open"
							href={resource.url}
							target="_blank"
							rel="noopener noreferrer"
						>
							<span className="card-thumb">
								<img src={resource.url} alt={resource.name} loading="lazy" />
							</span>
							<span className="card-name">{resource.name}</span>
							<span className="card-meta">{formatSize(resource.size)}</span>
						</a>

						<div className="card-actions">
							<button
								type="button"
								className="btn grey"
								aria-label={`Copy link of ${resource.name}`}
								onClick={() => this.copyUrl(resource)}
							>Copy link</button>
							<button
								type="button"
								className="btn grey"
								aria-label={`Move ${resource.name}`}
								onClick={() => this.modal('modal_move', 'opened', resource)}
							>Move</button>
							<button
								type="button"
								className="btn grey"
								aria-label={`Delete ${resource.name}`}
								onClick={() => this.modal('modal_delete', 'opened', {type: "resource", resource})}
							>Delete</button>
						</div>
					</div>
				))}
			</div>
		);
	}

	renderDeleteModal() {
		const {delete_target} = this.state;

		if (!delete_target) {
			return null;
		}

		const isFolder = delete_target.type === "folder";
		const count = isFolder ? delete_target.folder.count : 0;

		return (
			<div className="content">
				{isFolder && (
					<p className="text">
						{count > 0
							? <>Deleting <b>{delete_target.folder.name}</b> also deletes the {count} image{count === 1 ? '' : 's'} inside it.<br /></>
							: <>The folder <b>{delete_target.folder.name}</b> is empty.<br /></>}
						<b>Are you sure you want to delete it?</b>
					</p>
				)}

				{!isFolder && (
					<p className="text">
						This removes <b>{delete_target.resource.name}</b> for good.<br />
						<b>Are you sure you want to delete it?</b>
					</p>
				)}

				<button type="button" onClick={() => this.modal('modal_delete', 'closed')} className="btn grey">No</button>
				<button type="button" onClick={() => this.confirmDelete()} className="btn blue-light btn-delete">Yes</button>
			</div>
		);
	}

	render() {
		const {
			loading, error, notice, quota, folders, open_folder,
			uploading, upload_progress, folder_being_renamed, folder_name, move_target
		} = this.state;

		const current = this.openFolder();
		const full = quota.used >= quota.max;

		return (
			<div data-page="resources">
				<Header />

				<TitlePage title="Resources" />

				<div className="container-fluid" data-component="resources-content">
					<div className="container">
						<div className="resources-bar">
							<div className="resources-breadcrumb">
								{open_folder === null && <span className="crumb current">All resources</span>}

								{open_folder !== null && (
									<>
										<button
											type="button"
											className="crumb link"
											onClick={() => this.setState({open_folder: null, notice: "", error: ""})}
										>All resources</button>
										<span className="crumb-separator" aria-hidden="true">/</span>
										<span className="crumb current">{current ? current.name : ""}</span>
									</>
								)}
							</div>

							<div className="resources-actions">
								<p className={`resources-quota ${full ? 'is-full' : ''}`}>
									{quota.used} / {quota.max} images
								</p>

								<button
									type="button"
									className="btn grey"
									onClick={() => this.modal('modal_folder', 'opened')}
								>New folder</button>

								{/* El input vive escondido porque el de verdad no se puede
									estilar; el boton visible lo dispara por referencia. */}
								<input
									ref={this.fileInputRef}
									type="file"
									className="visually-hidden"
									accept="image/*"
									multiple
									onChange={this.onFilesPicked}
								/>

								<button
									type="button"
									className="btn blue"
									disabled={uploading || full}
									onClick={this.pickFiles}
								>
									{uploading ? 'Uploading…' : 'Upload images'}
								</button>
							</div>
						</div>

						<p className="resources-hint">
							Images only, up to {quota.max_file_size_mb} MB each. {quota.max} per account.
							{/* La cuota no es negociable desde aqui, asi que la linea dice a
								donde ir si hace falta mas en vez de dejar al usuario atascado. */}
							<span className="resources-contact">(Please contact Seat 7 if you require more files)</span>
						</p>

						{upload_progress && (
							<div className="resources-progress">
								<p className="text">Uploading {upload_progress.current} of {upload_progress.total}…</p>
							</div>
						)}

						{notice && (
							<div className="resources-notice">
								<p className="text">{notice}</p>
							</div>
						)}

						{error && (
							<div className="resources-error">
								<p className="text">{error}</p>
							</div>
						)}

						{loading && (
							<div className="resources-empty">
								<p className="text">Loading your resources…</p>
							</div>
						)}

						{!loading && this.renderGrid()}
					</div>
				</div>

				<div data-component="modal" className={`modal-project-edit h-auto ${this.state.modal_folder}`}>
					<div className="modal-container">
						<div className="modal-header">
							<p className="text">{folder_being_renamed ? 'Rename folder' : 'New folder'}</p>
							<button
								type="button"
								onClick={() => this.modal('modal_folder', 'closed')}
								className="btn btn-close"
								aria-label="Close"
							></button>
						</div>

						<div className="modal-content modal-folder">
							<form onSubmit={e => this.saveFolder(e)}>
								<input
									autoComplete="off"
									name="folder_name"
									type="text"
									placeholder="Folder name"
									value={folder_name}
									onChange={e => this.onChange(e)}
								/>

								<button type="submit" className="btn blue btn-save">Save</button>
							</form>
						</div>
					</div>
				</div>

				<div data-component="modal" className={`modal-project-edit h-auto ${this.state.modal_move}`}>
					<div className="modal-container">
						<div className="modal-header">
							<p className="text">Move image</p>
							<button
								type="button"
								onClick={() => this.modal('modal_move', 'closed')}
								className="btn btn-close"
								aria-label="Close"
							></button>
						</div>

						<div className="modal-content modal-move">
							<p className="move-title">
								{move_target ? `Where should "${move_target.name}" go?` : ''}
							</p>

							<div className="move-options">
								<button
									type="button"
									className="btn grey"
									disabled={!!move_target && !move_target.folder_id}
									onClick={() => this.moveTo(null)}
								>All resources (root)</button>

								{folders.map(folder => (
									<button
										type="button"
										className="btn grey"
										key={`move-${folder.id}`}
										disabled={!!move_target && move_target.folder_id === folder.id}
										onClick={() => this.moveTo(folder.id)}
									>{folder.name}</button>
								))}
							</div>

							{folders.length === 0 && (
								<p className="move-empty">You have no folders yet. Create one first.</p>
							)}
						</div>
					</div>
				</div>

				<div data-component="modal" className={`modal-cancel modal-delete-resource ${this.state.modal_delete}`}>
					{this.renderDeleteModal()}
				</div>
			</div>
		);
	}
}

// Los bytes crudos no dicen nada en una tarjeta; KB y MB si.
const formatSize = (bytes) => {
	if (!bytes && bytes !== 0) {
		return '';
	}

	if (bytes < 1024) {
		return `${bytes} B`;
	}

	if (bytes < 1024 * 1024) {
		return `${Math.round(bytes / 1024)} KB`;
	}

	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
